import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ExternalLink, BarChart3, Headphones, Clock, Sparkles, GitBranch, GitMerge, Shield, DollarSign, Building2 } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'

interface Application {
  id: string
  name: string
  description: string
  url: string
  icon_url: string
  app_type?: 'native' | 'external'
}

export const Dashboard: React.FC = () => {
  const [applications, setApplications] = useState<Application[]>([])
  const [loading, setLoading] = useState(true)
  const [userProfile, setUserProfile] = useState<{ full_name: string | null }>({ full_name: null })
  const { user } = useAuth()
  const navigate = useNavigate()
  
  // Admin user ID
  const ADMIN_USER_ID = '89927d9d-5bb1-41af-8755-01ffd69d960f'
  const isAdmin = user?.id === ADMIN_USER_ID

  const handleSelectApp = (app: Application) => {
    navigate(`/app/${app.id}`)
  }

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return

      // Fetch user profile
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('full_name')
        .eq('id', user.id)
        .single()

      if (profile) {
        setUserProfile(profile)
      }

      // Fetch all applications from database (both native and external)
      const { data, error } = await supabase
        .from('applications')
        .select(`
          id,
          name,
          description,
          url,
          icon_url,
          app_type,
          user_app_access!inner(user_id)
        `)
        .eq('user_app_access.user_id', user.id)
        .eq('is_active', true)

      if (error) {
        console.error('Error fetching applications:', error)
      } else {
        setApplications(data || [])
      }
      setLoading(false)
    }

    fetchData()
  }, [user])

  const getIconComponent = (appName: string) => {
    switch (appName.toLowerCase()) {
      case 'support ticket dashboard':
        return <Headphones style={{ height: '32px', width: '32px', color: '#FF6B35' }} />
      case 'month end reporting':
        return <BarChart3 style={{ height: '32px', width: '32px', color: '#FFAB00' }} />
      case 'tech roadmap':
        return <GitBranch style={{ height: '32px', width: '32px', color: '#8b5cf6' }} />
      case '2025 funders':
        return <DollarSign style={{ height: '32px', width: '32px', color: '#10b981' }} />
      case 'company merge review':
        return <GitMerge style={{ height: '32px', width: '32px', color: '#f59e0b' }} />
      case 'all companies':
        return <Building2 style={{ height: '32px', width: '32px', color: '#2563eb' }} />
      default:
        return <ExternalLink style={{ height: '32px', width: '32px', color: '#2563eb' }} />
    }
  }

  const getIconBackground = (appName: string) => {
    switch (appName.toLowerCase()) {
      case 'support ticket dashboard':
        return '#fff5f5'
      case 'month end reporting':
        return '#fffbeb'
      case 'tech roadmap':
        return '#f3e8ff'
      case '2025 funders':
        return '#f0fdf4'
      case 'company merge review':
        return '#fef3c7'
      case 'all companies':
        return '#eff6ff'
      default:
        return '#eff6ff'
    }
  }

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(to bottom right, #f1f5f9, #dbeafe, #e0e7ff)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            width: '64px', 
            height: '64px', 
            background: 'white', 
            borderRadius: '50%', 
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', 
            marginBottom: '16px' 
          }}>
            <div style={{ 
              animation: 'spin 1s linear infinite', 
              borderRadius: '50%', 
              height: '32px', 
              width: '32px', 
              border: '2px solid #2563eb', 
              borderBottomColor: 'transparent' 
            }}></div>
          </div>
          <p style={{ 
            color: '#6b7280', 
            fontWeight: '500',
            fontFamily: 'Lexend Deca, sans-serif'
          }}>
            Loading your applications...
          </p>
        </div>
      </div>
    )
  }

  const currentTime = new Date().getHours()
  const getGreeting = () => {
    if (currentTime < 12) return 'Good morning'
    if (currentTime < 17) return 'Good afternoon'
    return 'Good evening'
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(to bottom right, #f1f5f9, #dbeafe, #e0e7ff)'
    }}>
      <div style={{ 
        maxWidth: '1280px', 
        margin: '0 auto', 
        padding: '48px 16px'
      }}>
        <div style={{ marginBottom: '48px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginBottom: '16px' 
          }}>
            <Sparkles style={{ 
              height: '32px', 
              width: '32px', 
              color: '#FFAB00', 
              marginRight: '12px' 
            }} />
            <h2 style={{ 
              fontSize: '36px', 
              fontWeight: 'bold', 
              color: '#111827',
              fontFamily: 'Lexend Deca, sans-serif'
            }}>
              {getGreeting()}!
            </h2>
          </div>
          <p style={{ 
            fontSize: '20px', 
            color: '#6b7280', 
            marginBottom: '8px',
            fontFamily: 'Lexend Deca, sans-serif'
          }}>
            Welcome back, {userProfile.full_name || user?.email?.split('@')[0]}
          </p>
          <p style={{
            fontSize: '18px',
            color: '#9ca3af',
            fontFamily: 'Lexend Deca, sans-serif'
          }}>
            Select an application to get started
          </p>
        </div>

        {/* Admin Panel Button */}
        {isAdmin && (
          <div style={{ marginBottom: '32px' }}>
            <button
              onClick={() => navigate('/admin')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '16px 32px',
                background: 'linear-gradient(to right, #dc2626, #b91c1c)',
                color: 'white',
                fontWeight: '600',
                borderRadius: '12px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s',
                boxShadow: '0 10px 15px -3px rgba(220, 38, 38, 0.3)'
              }}
              onMouseEnter={(e) => {
                const target = e.currentTarget as HTMLButtonElement;
                target.style.transform = 'translateY(-2px)';
                target.style.boxShadow = '0 20px 25px -5px rgba(220, 38, 38, 0.4)';
              }}
              onMouseLeave={(e) => {
                const target = e.currentTarget as HTMLButtonElement;
                target.style.transform = 'translateY(0)';
                target.style.boxShadow = '0 10px 15px -3px rgba(220, 38, 38, 0.3)';
              }}
            >
              <Shield style={{ height: '20px', width: '20px', marginRight: '8px' }} />
              Admin Panel
            </button>
          </div>
        )}

        {applications.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '64px 0' }}>
            <div style={{ 
              background: 'rgba(255, 255, 255, 0.95)', 
              borderRadius: '24px', 
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', 
              padding: '48px', 
              maxWidth: '448px', 
              margin: '0 auto',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <div style={{ 
                margin: '0 auto 24px', 
                height: '64px', 
                width: '64px', 
                background: '#f3f4f6', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <ExternalLink style={{ height: '32px', width: '32px', color: '#9ca3af' }} />
              </div>
              <h3 style={{ 
                fontSize: '24px', 
                fontWeight: 'bold', 
                color: '#111827', 
                marginBottom: '16px',
                fontFamily: 'Lexend Deca, sans-serif'
              }}>
                No applications available
              </h3>
              <p style={{ 
                color: '#6b7280', 
                marginBottom: '24px',
                fontFamily: 'Lexend Deca, sans-serif'
              }}>
                Contact your administrator to request access to applications.
              </p>
              <button style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '12px 24px',
                background: 'white',
                color: '#2563eb',
                fontWeight: '600',
                borderRadius: '12px',
                border: '2px solid #2563eb',
                cursor: 'pointer',
                fontSize: '16px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                const target = e.currentTarget as HTMLButtonElement;
                target.style.background = '#2563eb';
                target.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                const target = e.currentTarget as HTMLButtonElement;
                target.style.background = 'white';
                target.style.color = '#2563eb';
              }}>
                <Clock style={{ height: '16px', width: '16px', marginRight: '8px' }} />
                Request Access
              </button>
            </div>
          </div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', 
            gap: '32px' 
          }}>
            {applications.map((app) => (
              <div
                key={app.id}
                onClick={() => handleSelectApp(app)}
                style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '24px',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  padding: '32px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  backdropFilter: 'blur(8px)'
                }}
                onMouseEnter={(e) => {
                  const target = e.currentTarget as HTMLDivElement;
                  target.style.transform = 'scale(1.02)';
                  target.style.boxShadow = '0 25px 50px -12px rgba(0, 0, 0, 0.25)';
                }}
                onMouseLeave={(e) => {
                  const target = e.currentTarget as HTMLDivElement;
                  target.style.transform = 'scale(1)';
                  target.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                  <div style={{ 
                    flexShrink: 0, 
                    padding: '16px', 
                    borderRadius: '12px', 
                    background: getIconBackground(app.name) 
                  }}>
                    {getIconComponent(app.name)}
                  </div>
                  <div style={{ marginLeft: '16px', flex: 1 }}>
                    <h3 style={{ 
                      fontSize: '20px', 
                      fontWeight: 'bold', 
                      color: '#111827',
                      fontFamily: 'Lexend Deca, sans-serif'
                    }}>
                      {app.name}
                    </h3>
                  </div>
                </div>
                <p style={{ 
                  color: '#6b7280', 
                  marginBottom: '24px', 
                  lineHeight: '1.6',
                  fontFamily: 'Lexend Deca, sans-serif'
                }}>
                  {app.description}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <button style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '12px 20px',
                    background: 'linear-gradient(to right, #2563eb, #4f46e5)',
                    color: 'white',
                    fontWeight: '600',
                    borderRadius: '12px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontFamily: 'Lexend Deca, sans-serif',
                    transition: 'all 0.2s',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectApp(app);
                  }}
                  onMouseEnter={(e) => {
                    e.stopPropagation();
                    const target = e.currentTarget as HTMLButtonElement;
                    target.style.background = 'linear-gradient(to right, #1d4ed8, #4338ca)';
                    target.style.boxShadow = '0 10px 15px -3px rgba(37, 99, 235, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.stopPropagation();
                    const target = e.currentTarget as HTMLButtonElement;
                    target.style.background = 'linear-gradient(to right, #2563eb, #4f46e5)';
                    target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                  }}>
                    <span>Open app</span>
                    <ExternalLink style={{ marginLeft: '8px', height: '16px', width: '16px' }} />
                  </button>
                  <div style={{ 
                    height: '8px', 
                    width: '8px', 
                    background: '#10b981', 
                    borderRadius: '50%' 
                  }}></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}